import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FinancialService } from '../../../../services/financial.service';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { PaginatorModule } from 'primeng/paginator';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { CustomerService } from '../../../../services/customer.service';
import { CustomerRevenueSummary } from '../../../../models/view-models/customerRevenueSummary.viewmodel';
import { FinancialDashboard } from '../../../../models/financialDashboard.model';
import { DateRangeEnum } from '../../../../models/enums/dateRange.enum';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { City } from '../../../../models/city.model';
import { State } from '../../../../models/state.model';
import { StateService } from '../../../../services/state.service';
import { CityService } from '../../../../services/city.service';


@Component({
  selector: 'app-list-financial',
  standalone: true,
  templateUrl: './list-financial.component.html',
  styleUrls: ['./list-financial.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    CardModule,
    ChartModule,
    PaginatorModule,
    TableModule,
    TagModule,
  ],
  providers: [FinancialService, CustomerService]
})
export class ListFinancialComponent implements OnDestroy {
  financials: CustomerRevenueSummary[] = [];
  searchTerm = '';
  currentStatus = 'pendente';
  currentDateRange = DateRangeEnum.All;
  loading = false;
  hoverEdit = false;
  hoverView = false;
  states: State[] = [];
  cities: City[] = [];
  selectedState: string = 'AL';
  selectedCity: string | null = null;
  searchCpf = '';
  searchId: number | null = null;

  showDashboard: boolean = false
  
  
  financialDashboard: FinancialDashboard = {
    totalRevenues: 0,
    totalValueSold: 0,
    totalValuePending: 0,
    totalValuePaid: 0,
    totalValueCanceled: 0,
    activeCustomers: 0,
    totalPending: 0,
    pendingRevenues: 0,
    paidRevenues: 0,
    totalValueSoldChartData: {
      labels: [],
      data: []
    },
    totalValuePendingChartData: {
      labels: [],
      data: []
    },
    totalValuePaidChartData: {
      labels: [],
      data: []
    },
    totalValueCanceledChartData: {
      labels: [],
      data: []
    }
  };

  
  first = 0;
  rows = 5;
  totalRecords = 0;
  currentPage = 1;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  private idSubject = new Subject<number | null>();
  private idSubscription?: Subscription;
  private idSearch: number | null = null;

  private cpfSubject = new Subject<string>();
  private cpfSubscription?: Subscription;
  private cpfSearch = '';

  /** Evita loop entre leitura da URL e `router.navigate` com query params. */
  private syncingFromQuery = false;

  /** Evita que o p-dropdown de UF dispare `onChange` ao montar opções e limpe `cidade` vinda da URL. */
  private allowStateFilterChange = false;

  barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { 
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          title: function(context: any[]) {
            return context[0].label;
          },
          label: function(context: any) {
            return 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
          }
        }
      }
    },
    scales: {
      x: { display: false, grid: { display: false } },
      y: { 
        display: false, 
        grid: { display: false },
        min: 0,
        max: function(context: any) {
          const values = context.chart.data.datasets[0].data;
          if (!values || values.length === 0) return 100;
          const maxValue = Math.max(...values);
          return maxValue * 1.1;
        }
      }
    },
    elements: {
      bar: { borderRadius: 4 }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    layout: {
      padding: {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }
    }
  };

  statusOptions = [
    { label: 'Todos', value: 'todos' },
    { label: 'Pendente', value: 'pendente' },
    { label: 'Normal', value: 'normal' },
    { label: 'Quitado', value: 'quitado' },
    { label: 'Cancelado', value: 'cancelado' }
  ];

  dateRangeOptions = [
    { label: 'Todos os períodos', value: DateRangeEnum.All },
    { label: 'Este mês', value: DateRangeEnum.ThisMonth },
    { label: 'Mês passado', value: DateRangeEnum.LastMonth },
    { label: 'Últimos 3 meses', value: DateRangeEnum.LastThreeMonths },
    { label: 'Últimos 6 meses', value: DateRangeEnum.LastSixMonths },
    { label: 'Este ano', value: DateRangeEnum.ThisYear },
    { label: 'Ano passado', value: DateRangeEnum.LastYear }
  ];

  private chartConfig = {
    totalValueSold: { color: '#f97316', label: 'Valor Vendido' },
    totalValuePending: { color: '#f59e0b', label: 'Valor Pendente' },
    totalValuePaid: { color: '#10b981', label: 'Valor Pago' },
    totalValueCanceled: { color: '#ef4444', label: 'Valor Cancelado' }
  };

  constructor(
    private financialService: FinancialService,
    private customerService: CustomerService,
    private stateService: StateService,
    private cityService: CityService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm = term;
      this.loadClients();
      this.loadDashboard();
      this.updateQueryParamsFromState();
    });

    this.cpfSubscription = this.cpfSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.cpfSearch = term;
      this.loadClients();
      this.loadDashboard();
      this.updateQueryParamsFromState();
    });

    this.idSubscription = this.idSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.idSearch = term;
      this.loadClients();
      this.loadDashboard();
      this.updateQueryParamsFromState();
    });
  }

  ngOnInit(): void {
    this.applyFiltersFromQuery(this.route.snapshot.queryParamMap);
    this.loadClients();
    this.loadDashboard();
    this.loadStates();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.cpfSubscription?.unsubscribe();
    this.idSubscription?.unsubscribe();
  }

  private applyFiltersFromQuery(q: ParamMap): void {
    this.syncingFromQuery = true;
    try {
      const nome = q.get('q');
      if (nome !== null) {
        this.searchTerm = nome;
      }

      const cpf = q.get('cpf');
      if (cpf !== null) {
        this.searchCpf = cpf;
        this.cpfSearch = cpf;
      }

      const codigo = q.get('codigo');
      if (codigo !== null && codigo !== '') {
        const n = parseInt(codigo, 10);
        if (!isNaN(n)) {
          this.searchId = n;
          this.idSearch = n;
        }
      } else {
        this.searchId = null;
        this.idSearch = null;
      }

      const status = q.get('status');
      if (status !== null && this.statusOptions.some((o) => o.value === status)) {
        this.currentStatus = status;
      }

      const periodo = q.get('periodo');
      if (periodo !== null && periodo !== '') {
        const p = parseInt(periodo, 10);
        if (!isNaN(p) && p >= DateRangeEnum.All && p <= DateRangeEnum.LastYear) {
          this.currentDateRange = p;
        }
      }

      const uf = q.get('uf');
      if (uf !== null && uf.length >= 2) {
        this.selectedState = uf;
      }

      const cidade = q.get('cidade');
      if (cidade !== null) {
        this.selectedCity = cidade === '' ? null : cidade;
      }

      const pageStr = q.get('page');
      if (pageStr !== null && pageStr !== '') {
        const pg = parseInt(pageStr, 10);
        if (!isNaN(pg) && pg >= 1) {
          this.currentPage = pg;
        }
      }

      const rowsStr = q.get('rows');
      if (rowsStr !== null && rowsStr !== '') {
        const r = parseInt(rowsStr, 10);
        if (!isNaN(r) && r > 0) {
          this.rows = r;
        }
      }

      this.first = (this.currentPage - 1) * this.rows;

      const dash = q.get('dashboard');
      if (dash === '1' || dash === 'true') {
        this.showDashboard = true;
      } else if (dash === '0' || dash === 'false') {
        this.showDashboard = false;
      }
    } finally {
      this.syncingFromQuery = false;
    }
  }

  private buildQueryParamsFromState(): Params {
    const params: Params = {
      status: this.currentStatus,
      periodo: String(this.currentDateRange),
      uf: this.selectedState
    };

    const q = this.searchTerm?.trim();
    if (q) {
      params['q'] = q;
    }

    const cpf = (this.searchCpf ?? this.cpfSearch)?.trim();
    if (cpf) {
      params['cpf'] = cpf;
    }

    const idVal = this.searchId ?? this.idSearch;
    if (idVal != null && !isNaN(Number(idVal))) {
      params['codigo'] = String(idVal);
    }

    if (this.selectedCity) {
      params['cidade'] = this.selectedCity;
    }

    if (this.currentPage > 1) {
      params['page'] = String(this.currentPage);
    }

    if (this.rows !== 5) {
      params['rows'] = String(this.rows);
    }

    if (this.showDashboard) {
      params['dashboard'] = '1';
    }

    return params;
  }

  private updateQueryParamsFromState(): void {
    if (this.syncingFromQuery) {
      return;
    }
    const queryParams = this.buildQueryParamsFromState();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  loadStates(): void {
    this.stateService.getAll('', false, null).subscribe({
      next: (data) => {
        this.states = data.data || [];
        this.loadCities(() => {
          setTimeout(() => {
            this.allowStateFilterChange = true;
          }, 0);
        });
      }
    });
  }

  changeDashboard(): void {
    this.showDashboard = !this.showDashboard;
    this.updateQueryParamsFromState();
  }

  onStateChange(): void {
    if (!this.allowStateFilterChange) {
      return;
    }
    this.selectedCity = null;
    this.currentPage = 1;
    this.first = 0;
    this.loadCities();
    this.loadClients();
    this.loadDashboard();
    this.updateQueryParamsFromState();
  }

  loadCities(onComplete?: () => void): void {
    this.cityService.getAll('', null, this.selectedState, false, null).subscribe({
      next: (data) => {
        this.cities = data.data || [];
        onComplete?.();
      }
    });
  }

  loadClients(): void {
    this.loading = true;
    this.customerService.getCustomersRevenueSummary(this.searchTerm, this.cpfSearch, this.idSearch, this.currentStatus, this.currentDateRange, this.selectedCity, this.currentPage).subscribe({
      next: (data) => {
        this.financials = data?.data ?? [];
        this.totalRecords = data.pagination.totalRecords ?? this.financials.length;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onSearchChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.searchSubject.next(this.searchTerm);
  }

  onSearchCpfChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.cpfSubject.next(this.searchCpf);
  }

  onSearchIdChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.idSubject.next(this.searchId);
  }

  onStatusChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.loadClients();
    this.loadDashboard();
    this.updateQueryParamsFromState();
  }

  onDateRangeChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.loadClients();
    this.loadDashboard();
    this.updateQueryParamsFromState();
  }

  onCityChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.loadClients();
    this.loadDashboard();
    this.updateQueryParamsFromState();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.currentPage = Math.floor(this.first / this.rows) + 1;
    this.loadClients();
    this.updateQueryParamsFromState();
  }

  createCustomer(): void {
    this.router.navigate(['/app/financial/new']);
  }

  editRevenue(id: number): void {
    this.router.navigate([`/app/financial/edit/${id}`]);
  }

  goToRevenues(customer: CustomerRevenueSummary): void {
    const queryParams = this.buildQueryParamsFromState();
    // Não usar `if (ok)` no then: quando a URL já coincide com os params, o Router pode
    // resolver com `false` e a navegação para o detalhe deixava de ocorrer.
    void this.router
      .navigate([], { relativeTo: this.route, queryParams, replaceUrl: true })
      .finally(() => {
        void this.router.navigate([`/app/financial/view/${customer.customerId}`]);
      });
  }

  printHistory(customerId: number)
  {
    this.financialService.exportHistoryA4(customerId).subscribe({
      next: (data) => {
        const blob = new Blob([data], { type: 'application/pdf' }); 
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      },
      error: (error) => {
        console.error('Error generating PDF:', error);
      }
    });
  }

  formatCustomer(customer: any): string {
  return JSON.stringify(customer, null, 2);
}

  loadDashboard(): void {
    this.financialService.getDashboard(this.searchTerm, this.currentStatus, this.selectedCity, this.currentDateRange).subscribe({
      next: (data) => {
        this.financialDashboard = data.data;
      },
      error: (error) => {
        console.error('Error loading dashboard summary:', error);
      }
    });
  }

  getChartData(chartType: keyof typeof this.chartConfig) {
    const config = this.chartConfig[chartType];
    const chartData = this.financialDashboard[`${chartType}ChartData` as keyof FinancialDashboard] as any;
    
    return {
      labels: chartData?.labels || [],
      datasets: [
        {
          data: chartData?.data || [],
          backgroundColor: config.color,
          borderColor: config.color,
          borderWidth: 1,
          borderRadius: 4
        }
      ]
    };
  }

  
  getTotalValueSoldChartData() {
    return this.getChartData('totalValueSold');
  }

  getTotalValuePendingChartData() {
    return this.getChartData('totalValuePending');
  }

  getTotalValuePaidChartData() {
    return this.getChartData('totalValuePaid');
  }

  getTotalValueCanceledChartData() {
    return this.getChartData('totalValueCanceled');
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'quitado':
        return 'success';
      case 'pendente':
        return 'warn';
      case 'normal':
        return 'info';
      case 'cancelado':
        return 'danger';
      default:
        return 'info';
    }
  }

  getCustomerAddress(customer: CustomerRevenueSummary): string {
    return [customer.address, customer.addressNumber, customer.district, customer.city, customer.state]
      .filter(Boolean)
      .join(', ');
  }
}